import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Rating,
} from "@mui/material";
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  FormatQuote as QuoteIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const TESTIMONIALS = [
  {
    content:
      "Jeremy Solutions delivered an outstanding website for our company. Their creativity and attention to detail made the project a huge success.",
    author: "John Smith",
    position: "CEO, TechCorp",
    rating: 5,
    avatar: "J", // First letter of name if no image
    color: "primary",
  },
  {
    content:
      "We approached Jeremy Solutions for a custom GIS solution, and they exceeded our expectations with a user-friendly and powerful platform.",
    author: "Sarah Johnson",
    position: "Project Manager, MapTech",
    rating: 5,
    avatar: "S",
    color: "secondary",
  },
  {
    content:
      "The digital marketing strategies implemented by Jeremy Solutions transformed our online presence, driving more traffic and conversions.",
    author: "David Wilson",
    position: "Marketing Director, GrowthCo",
    rating: 5,
    avatar: "D",
    color: "info",
  },
  {
    content:
      "Thanks to Jeremy Solutions, our business now benefits from a modern, responsive web design that has enhanced our brand image.",
    author: "Emily Brown",
    position: "Owner, StyleHub",
    rating: 5,
    avatar: "E",
    color: "success",
  },
  {
    content:
      "Jeremy Solutions provided excellent customer support during our GIS implementation project, ensuring our team was fully equipped.",
    author: "Michael Chen",
    position: "CTO, DataSys",
    rating: 5,
    avatar: "M",
    color: "warning",
  },
];

export default function Testimonials() {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(timer);
  }, [currentIndex]);

  const handlePrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.primary.main,
            0.04
          )} 0%, transparent 100%)`,
        },
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={6}>
          <Stack spacing={2} sx={{ textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              Client Testimonials
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Hear what our clients have to say about their experience working
              with us
            </Typography>
          </Stack>

          <Box sx={{ position: "relative", minHeight: 300 }}>
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                style={{
                  position: "absolute",
                  width: "100%",
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: "100%",
                    bgcolor: "background.paper",
                    border: `1px solid ${alpha(
                      theme.palette[TESTIMONIALS[currentIndex].color].main,
                      0.1
                    )}`,
                    borderRadius: 4,
                  }}
                >
                  <Stack spacing={3} alignItems="center">
                    <Box
                      sx={{
                        position: "relative",
                        mb: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          bgcolor: alpha(
                            theme.palette[TESTIMONIALS[currentIndex].color]
                              .main,
                            0.1
                          ),
                          color: `${TESTIMONIALS[currentIndex].color}.main`,
                          fontSize: 32,
                          fontWeight: 700,
                        }}
                      >
                        {TESTIMONIALS[currentIndex].avatar}
                      </Avatar>
                      <QuoteIcon
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -20,
                          fontSize: 40,
                          color: alpha(
                            theme.palette[TESTIMONIALS[currentIndex].color]
                              .main,
                            0.2
                          ),
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: "1rem", md: "1.25rem" },
                        fontStyle: "italic",
                        textAlign: "center",
                        color: "text.secondary",
                        maxWidth: 800,
                        mx: "auto",
                      }}
                    >
                      "{TESTIMONIALS[currentIndex].content}"
                    </Typography>

                    <Stack spacing={1} alignItems="center">
                      <Rating
                        value={TESTIMONIALS[currentIndex].rating}
                        readOnly
                        sx={{
                          color: `${TESTIMONIALS[currentIndex].color}.main`,
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {TESTIMONIALS[currentIndex].author}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {TESTIMONIALS[currentIndex].position}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              </motion.div>
            </AnimatePresence>

            <Stack
              direction="row"
              spacing={2}
              sx={{
                position: "absolute",
                bottom: -60,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={handlePrevious}
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    bgcolor: "background.paper",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <PrevIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    bgcolor: "background.paper",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <NextIcon />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
